import { useSyncExternalStore } from "react";
import type { PhoneOperator, Session } from "ys-webrtc-sdk-core";
import { telephonyService } from "./telephonyService";
import { soundSystem } from "./soundSystem";
import { AudioZustand, KEIN_TON_NACH_MS, keinTonGrund, keinTonNachAnnahme } from "./softphoneKeinTon";
import {
  ANNAHME_BESTAETIGUNG_MS,
  endeGrundAus,
  ergebnisText,
  fuehreAnnehmenAus,
  type AnnehmenErgebnis,
} from "./softphoneAnnehmen";

/**
 * Phase 3/4 — Linkus Web SDK softphone (In-App-Audio + Call-Control).
 *
 * Additive, feature-geflaggte Schicht: das SSE-Inbound-Popup (CallWidget/
 * TelephonyContext) bleibt unverändert. Kapselt das Yeastar "Linkus SDK for Web
 * Core" (npm: ys-webrtc-sdk-core) gegen dessen mitgelieferte TypeScript-Typen.
 *
 * - Audio: das SDK liefert pro Session einen `remoteStream` (MediaStream); der
 *   wird an ein verstecktes <audio autoplay> gehängt → man hört das Gespräch.
 * - Call-Control: alle phone-Methoden brauchen die callId der aktiven Session.
 * - Das SDK wird per dynamischem Import geladen (Code-Splitting; nur wenn das
 *   Feature aktiv ist).
 *
 * Aktiv nur bei TELEPHONY_SDK_ENABLED=true + PBX-seitig freigeschaltetem Linkus
 * SDK. Gegen die echte Anlage noch nicht smoke-getestet.
 */

export type SoftphoneStatus =
  | "idle"
  | "connecting"
  | "registered"
  | "unsupported"
  | "error";

export interface SoftphoneCall {
  id: string;
  number: string | null;
  name: string | null;
  direction: "inbound" | "outbound";
  /** ringing | calling | talking | connecting */
  state: string;
  isMute: boolean;
  isHold: boolean;
  /** ms-Timestamp der Annahme (accepted/confirmed); null bis zum Gesprächsbeginn. */
  answeredAt: number | null;
}

/**
 * Weiterleiten mit Rückfrage (08.10.2026): Der Anrufer wird gehalten, der
 * Kollege angerufen; erst nach dem Gespräch mit ihm wird verbunden — oder es
 * geht zurück zum Anrufer. So verlangt es das SDK: `call(ziel, …, anruferId)`
 * baut das Rückfragegespräch auf, `attendedTransfer(rueckfrageId, ziel)`
 * verbindet. Vorher rief KMVS `attendedTransfer` direkt auf dem Anrufer auf —
 * das SDK lehnte still ab („not found parent call id"), es passierte nichts.
 */
export interface SoftphoneRueckfrage {
  /** Der gehaltene Anrufer. */
  anruferCallId: string;
  anruferNummer: string | null;
  /** Durchwahl oder Nummer des Kollegen. */
  ziel: string;
  /** Das Gespräch mit dem Kollegen — null, bis das SDK es angelegt hat. */
  rueckfrageCallId: string | null;
  /** „Verbinden" gedrückt; die Anlage beendet dann beide Gespräche. */
  verbindetSeit: number | null;
  /** Hat die Anlage das Verbinden nicht bestätigt? (Text für das Fenster) */
  fehler: string | null;
}

export interface SoftphoneState {
  status: SoftphoneStatus;
  activeCall: SoftphoneCall | null;
  error: string | null;
  rueckfrage: SoftphoneRueckfrage | null;
}

/**
 * Ein Anruf aus Sicht dieses Wrappers — für die Telefonie-Diagnose im Ticket
 * (T-28). Zeiten als ISO-Strings; keine Gerätenamen.
 */
export interface SoftphoneAnrufDiagnose {
  callId: string;
  richtung: "inbound" | "outbound";
  nummer: string | null;
  /** Letzter bekannter SDK-callStatus (ringing/calling/talking/…) bzw. „beendet". */
  zustand: string;
  start: string;
  angenommen: string | null;
  ende: string | null;
  /** Höchste Zahl an Remote-Audio-Tracks, die während des Anrufs anlag. */
  tracksMax: number;
  /** Höchste Zahl davon, die live war (= es kamen Pakete an). */
  tracksLiveMax: number;
  /** Zustand des Remote-Audios KEIN_TON_NACH_MS nach der Annahme. */
  audioNachAnnahme: AudioZustand | null;
  /** Grund, falls nach der Annahme kein Ton anlag (sonst null). */
  keinTon: string | null;
  /** T-233: Zeitpunkt des Klicks auf „Annehmen"; null = nicht geklickt. */
  annehmenGeklickt: string | null;
  /** T-233: „angenommen" oder warum nicht (`softphoneAnnehmen.ts`). */
  annehmenErgebnis: string | null;
  /**
   * T-233: Warum der Anruf endete — Ereignis und SIP-Grund des SDK, etwa
   * „failed: Canceled" (Anrufer hat aufgelegt) oder „ended: Terminated".
   * Ohne ihn war nicht zu unterscheiden, ob der Anrufer aufgab oder die
   * Annahme scheiterte.
   */
  endeGrund: string | null;
}

export interface SoftphoneDiagnose {
  status: SoftphoneStatus;
  fehler: string | null;
  /** phone.isRegistered; null, wenn (noch) keine SDK-Instanz existiert. */
  registriert: boolean | null;
  aktiverAnruf: string | null;
  /** Die letzten Anrufe dieses Tabs, ältester zuerst. */
  anrufe: SoftphoneAnrufDiagnose[];
  /** Aktueller Zustand des Remote-Audios (null ohne Audio-Element). */
  audio: AudioZustand | null;
}

const initialState: SoftphoneState = { status: "idle", activeCall: null, error: null, rueckfrage: null };

/** Bestätigt die Anlage das Verbinden nicht binnen dieser Frist, sagt das Fenster es. */
const VERBINDEN_FRIST_MS = 6000;
/** Rückfragegespräche dieses Tabs — sie bekommen keine Nach-Anruf-Karte. */
const rueckfrageIds = new Set<string>();
/** Anrufer, die per Rückfrage übergeben wurden. */
const uebergebeneIds = new Set<string>();

let state: SoftphoneState = initialState;
const listeners = new Set<() => void>();

function emit(next: Partial<SoftphoneState>): void {
  state = { ...state, ...next };
  listeners.forEach((l) => l());
}

let phone: PhoneOperator | null = null;
let teardown: (() => void) | null = null;
let activeCallId: string | null = null;
/** Anrufe, deren answer() gerade beim SDK liegt — gegen den Doppelklick. */
const annehmenLaeuft = new Set<string>();
let connecting = false;
// Wird von disconnect() gesetzt, um eine in-flight connect()-Sequenz abzubrechen
// (z. B. Logout während des async Imports/Init): connect() prüft das Flag nach
// jedem await und räumt eine bereits erzeugte SDK-Instanz wieder ab, statt ein
// verwaistes, registriertes Softphone zurückzulassen.
let cancelConnect = false;
let audioEl: HTMLAudioElement | null = null;
/** Gewählte Gesprächsgeräte ("" = Standard), gesetzt von audioGeraete.ts. */
let mikrofonId = "";
let lautsprecherId = "";

/** Lautsprecher fürs Gespräch an das <audio> hängen (Edge/Chrome: setSinkId). */
function wendeLautsprecherAn(audio: HTMLAudioElement): void {
  const el = audio as HTMLAudioElement & { sinkId?: string; setSinkId?: (id: string) => Promise<void> };
  if (typeof el.setSinkId !== "function" || el.sinkId === lautsprecherId) return;
  el.setSinkId(lautsprecherId).catch((e: unknown) => {
    console.warn("[telephony] Lautsprecher nicht wählbar", { lautsprecherId, fehler: String(e) });
  });
}

/**
 * Mikrofon im laufenden Gespräch tauschen: neue Spur holen und am Sender der
 * PeerConnection ersetzen — kein Neuaufbau, der Anrufer merkt nichts.
 */
async function tauscheMikrofonImGespraech(): Promise<void> {
  const s = activeCallId && phone ? phone.getSession(activeCallId) : null;
  const pc = (s?.RTCSession as unknown as { connection?: RTCPeerConnection } | undefined)?.connection;
  const sender = pc?.getSenders?.().find((x) => x.track?.kind === "audio");
  if (!s || !sender) return;
  const alt = sender.track;
  const neu = await navigator.mediaDevices.getUserMedia({
    audio: mikrofonId ? { deviceId: { exact: mikrofonId } } : true,
  });
  const spur = neu.getAudioTracks()[0];
  if (!spur) return;
  if (alt) spur.enabled = alt.enabled; // stumm bleibt stumm
  await sender.replaceTrack(spur);
  const lokal = s.localStream;
  if (lokal && alt) {
    lokal.removeTrack(alt);
    lokal.addTrack(spur);
  }
  alt?.stop();
}

/** Per-call lifecycle tracking for the SDK→CallLog report (sent once at hang-up). */
interface CallMeta {
  startedAt: number;
  answeredAt: number | null;
  direction: "inbound" | "outbound";
  number: string | null;
}
const callMeta = new Map<string, CallMeta>();

/**
 * Ergebnis des Ingest je SDK-callId: die `CallLog`-ID der Zeile, die beim
 * Auflegen angelegt wurde (`null`, wenn der Ingest scheiterte). Die Notiz aus
 * dem Gespräch braucht genau diese ID, um sich an den Anruf zu hängen. Nur die
 * letzten Anrufe werden gemerkt — älteres findet der callId-Filter der Liste.
 */
const callLogIds = new Map<string, Promise<number | null>>();
const CALL_LOG_IDS_MAX = 20;

/**
 * Wer wissen will, dass ein Gespräch zu Ende ist. `activeCall` taugt dafür
 * nicht: Bei einem zweiten Anruf (Anklopfen) wechselt die aktive Session, ohne
 * dass der erste beendet wäre. Gefeuert wird genau einmal je callId, direkt
 * nachdem der Ingest losgeschickt wurde (`callLogIdFor` ist dann schon belegt).
 */
const endedListeners = new Set<(callId: string) => void>();

// Welche callIds bereits ihre Session-Listener gebunden haben — hält
// attachSession idempotent über die mehrfachen Phone-Events (incoming/
// newRTCSession/startSession feuern für EINEN Call) und die clearActive-
// Re-Attach hinweg. Ohne diese Sperre würden sich Listener auf dem
// langlebigen Singleton von Call zu Call unbegrenzt stapeln.
const attachedCallIds = new Set<string>();
// Pro-Call-Handle des Fallback-Timers; am Call-Ende gelöscht, damit kein
// veralteter Timer nach dem Verschwinden der Session noch feuert.
const fallbackTimers = new Map<string, number>();
// Pro-Call-Handle des Auflege-Fallbacks (P5): nach hangup()/reject() gesetzt;
// feuert nur, wenn KEIN ended/failed/bye/terminated-Event eintrifft, und räumt
// den Call dann hart ab — so bleibt nie ein „totes" Popup hängen. Am echten
// Call-Ende (cleanupCall) wieder gelöscht.
const hangupTimers = new Map<string, number>();
// Wie lange nach einem hangup()/reject() ohne SDK-End-Event gewartet wird, bevor
// der Call zwangsweise abgeräumt wird (P5-Lifecycle-Absicherung).
const HANGUP_FALLBACK_MS = 2500;
// Entprell-Handle für den Nachspiel-Versuch nach dem Stream-Event-Schwall (s. schedulePlayRetry).
let playRetryTimer: number | null = null;
// Letzter echter play()-Fehler mit anliegendem Stream (kein AbortError) — nur
// für die Diagnose; ein erfolgreiches play() setzt ihn zurück.
let letzterPlayFehler: string | null = null;

// ── Telefonie-Diagnose (T-28): kurze Anrufhistorie im Speicher ──
const ANRUF_HISTORIE_MAX = 5;
const anrufHistorie: SoftphoneAnrufDiagnose[] = [];
// Pro-Call-Handle der „Kein Ton?"-Prüfung KEIN_TON_NACH_MS nach der Annahme.
const keinTonTimers = new Map<string, number>();

function historieFuer(callId: string): SoftphoneAnrufDiagnose | undefined {
  for (let i = anrufHistorie.length - 1; i >= 0; i--) if (anrufHistorie[i].callId === callId) return anrufHistorie[i];
  return undefined;
}

function merkeAnruf(callId: string, direction: "inbound" | "outbound", number: string | null, zustand: string): void {
  if (historieFuer(callId)) return;
  anrufHistorie.push({
    callId, richtung: direction, nummer: number, zustand, start: new Date().toISOString(),
    angenommen: null, ende: null, tracksMax: 0, tracksLiveMax: 0, audioNachAnnahme: null, keinTon: null,
    annehmenGeklickt: null, annehmenErgebnis: null, endeGrund: null,
  });
  while (anrufHistorie.length > ANRUF_HISTORIE_MAX) anrufHistorie.shift();
}

function zaehleTracks(stream: MediaStream | null | undefined): { gesamt: number; live: number } {
  const tracks = stream?.getAudioTracks?.() ?? [];
  return { gesamt: tracks.length, live: tracks.filter((t) => t.enabled && !t.muted && t.readyState === "live").length };
}

/** Zustand des Remote-Audios jetzt: was am <audio> hängt und ob es spielt. */
function audioZustand(): AudioZustand | null {
  const audio = audioEl;
  if (!audio) return null;
  const src = audio.srcObject;
  const session = activeCallId && phone ? phone.getSession(activeCallId) : null;
  const stream = src instanceof MediaStream ? src : session?.remoteStream ?? null;
  const { gesamt, live } = zaehleTracks(stream);
  const sinkId = (audio as HTMLAudioElement & { sinkId?: string }).sinkId;
  return {
    hatStream: src instanceof MediaStream,
    tracksGesamt: gesamt,
    tracksLive: live,
    paused: audio.paused,
    readyState: audio.readyState,
    muted: audio.muted,
    sinkId: typeof sinkId === "string" ? sinkId : null,
    playFehler: letzterPlayFehler,
  };
}

/**
 * KEIN_TON_NACH_MS nach der Annahme einmal prüfen, ob Ton anliegt; wenn nicht,
 * in die Historie schreiben und als [telephony]-Warnung loggen (landet so in
 * den Telefonie-Spuren des Tickets). Das Anruf-Fenster zeigt den Hinweis
 * unabhängig davon selbst an.
 */
function planeKeinTonPruefung(callId: string, answeredAt: number): void {
  if (keinTonTimers.has(callId)) return;
  const t = window.setTimeout(() => {
    keinTonTimers.delete(callId);
    if (activeCallId !== callId) return;
    try {
      const z = audioZustand();
      const eintrag = historieFuer(callId);
      if (eintrag) eintrag.audioNachAnnahme = z;
      const gehalten = state.activeCall?.id === callId && state.activeCall.isHold;
      if (!keinTonNachAnnahme(z, { answeredAt, jetzt: Date.now(), gehalten })) return;
      const grund = keinTonGrund(z);
      if (eintrag) eintrag.keinTon = grund;
      console.warn("[telephony] kein Ton nach Annahme", { callId, grund, sdkStatus: state.status, ...z });
    } catch {
      /* Diagnose darf den Anruf nie stören */
    }
  }, KEIN_TON_NACH_MS);
  keinTonTimers.set(callId, t);
}

function ensureAudio(): HTMLAudioElement {
  if (!audioEl) {
    audioEl = document.createElement("audio");
    audioEl.autoplay = true;
    audioEl.muted = false;
    audioEl.volume = 1.0;
    // Safari/iOS: Inline-Wiedergabe erzwingen (kein Vollbild-/Player-Takeover).
    audioEl.setAttribute("playsinline", "");
    audioEl.style.display = "none";
    wendeLautsprecherAn(audioEl);
    // Self-Heal gegen den AbortError-Race: rund um die Annahme weist das SDK mehrfach
    // srcObject zu (initial/streamAdded/updateRemoteStream/peerconnection/fallback). Jede
    // Zuweisung ist ein "new load request", der ein laufendes play() abbricht → das Element
    // bleibt auf paused=false stehen, spielt aber nie wirklich (currentTime friert ein →
    // Stille). Ein persistenter canplay-Listener spielt nach JEDEM fertig geladenen Stream
    // erneut ab; so gewinnt immer der zuletzt anliegende Stream und die Wiedergabe heilt sich.
    audioEl.addEventListener("canplay", () => {
      void audioEl?.play().then(() => {
        letzterPlayFehler = null;
      }).catch(() => {
        /* AbortError/Autoplay: ein weiterer Load/Retry übernimmt — hier bewusst still */
      });
    });
    document.body.appendChild(audioEl);
  }
  return audioEl;
}

/**
 * Spielt das Remote-Audio explizit ab. MUSS nach JEDEM srcObject-Setzen UND im
 * Gesten-Fenster (answer/dial) aufgerufen werden: der Remote-Stream wird asynchron
 * (Event, ggf. Sekunden nach dem Klick) gesetzt — ohne expliziten play() blockt die
 * Browser-Autoplay-Policy die Wiedergabe → man hört den Gegenüber nicht.
 * Idempotent: mehrfaches play() ist unkritisch.
 */
function playAudio(reason: string): void {
  const audio = audioEl;
  if (!audio) return;
  const hasStream = !!audio.srcObject;
  const p = audio.play();
  if (!p || typeof p.then !== "function") return;
  p.then(() => {
    letzterPlayFehler = null;
    console.debug(`[telephony] audio.play() ok (${reason})`, {
      paused: audio.paused,
      hasStream,
    });
  }).catch((err: unknown) => {
    // AbortError = "interrupted by a new load request": rund um die Annahme weist das SDK
    // mehrfach srcObject zu und bricht das laufende play() ab. Erwartbar, KEIN Audio-Defekt
    // → nicht warnen, sondern nach kurzem Settle EINMAL nachspielen (der dann anliegende,
    // aktuelle Stream läuft). Der canplay-Listener heilt parallel.
    if ((err as { name?: string } | null)?.name === "AbortError") {
      schedulePlayRetry();
      return;
    }
    // Ohne gesetzten Stream ist eine Ablehnung erwartbar (Gesten-Prime, s. dial/answer).
    // Nur MIT anliegendem Stream ist ein blockiertes play() das eigentliche Audio-Problem.
    if (hasStream) {
      const e = err as { name?: string; message?: string } | null;
      letzterPlayFehler = `${e?.name ?? "Fehler"}${e?.message ? `: ${e.message}` : ""}`.slice(0, 200);
    }
    const log = hasStream ? console.warn : console.debug;
    log(
      `[telephony] audio.play() ${hasStream ? "blockiert/fehlgeschlagen" : "ohne Stream abgelehnt"} (${reason}); sdkStatus=${state.status}`,
      err,
    );
  });
}

/**
 * Entprellter Nachspiel-Versuch nach dem Stream-Event-Schwall: bündelt mehrere
 * AbortError-play()-Abbrüche zu EINEM play() auf dem zuletzt gesetzten srcObject,
 * sobald sich die Loads gelegt haben. Ergänzt den canplay-Self-Heal als Sicherheitsnetz.
 */
function schedulePlayRetry(): void {
  if (playRetryTimer !== null) return;
  playRetryTimer = window.setTimeout(() => {
    playRetryTimer = null;
    const audio = audioEl;
    if (!audio || !audio.srcObject) return;
    void audio.play().catch(() => {
      /* finaler Best-Effort; bei erneutem Load übernimmt canplay */
    });
  }, 250);
}

function toCall(s: Session | null | undefined): SoftphoneCall | null {
  if (!s) return null;
  const st = s.status;
  return {
    id: st.callId,
    number: st.number || null,
    name: st.name || null,
    direction: st.communicationType === "outbound" ? "outbound" : "inbound",
    state: st.callStatus,
    isMute: !!st.isMute,
    isHold: !!st.isHold,
    answeredAt: callMeta.get(st.callId)?.answeredAt ?? null,
  };
}

/**
 * Ruftöne (P3) zentral aus dem SDK-State ableiten — EINE Quelle der Wahrheit,
 * unabhängig vom React-Render-Lifecycle. soundSystem hält nur EINE Tonquelle und
 * ist idempotent, daher unkritisch gegen den Re-Entrancy-Guard / mehrfaches refresh().
 * - eingehend & noch nicht angenommen  → Klingelton
 * - ausgehend & noch nicht angenommen  → Ringback/Freizeichen
 * - angenommen (answeredAt/talking), beendet/abgelehnt, kein Call → Stille
 * Gewählt in linkusSdk (statt TelephonyContext), weil hier der maßgebliche
 * SDK-State entsteht und der Ton auch ohne gemountete UI korrekt stoppt. Pro
 * Browser-Tab läuft genau eine SDK-Instanz → genau ein Ton (kein Cross-Tab-Dedup nötig).
 */
function hasLiveRemoteAudio(session: Session | null | undefined): boolean {
  const stream = session?.remoteStream;
  if (!stream) return false;
  const tracks = stream.getAudioTracks?.() ?? [];
  return tracks.some((t) => t.enabled && !t.muted && t.readyState === "live");
}

function syncRingTones(): void {
  const call = state.activeCall;
  // Eingehend & noch nicht angenommen: das Remote-<audio> stummschalten. Sonst hört
  // man etwaiges PBX-In-Band-Audio (Komfort-/Wählton), das beim Klingeln bereits am
  // remoteStream anliegen kann, STATT unseres synthetischen Klingeltons (User-Report:
  // „eingehend spielt den Wählton"). Sobald angenommen (oder kein eingehendes Klingeln
  // mehr), wird das Element wieder hörbar geschaltet.
  const inboundRinging =
    !!call && call.direction === "inbound" && call.answeredAt === null && call.state !== "talking";
  if (audioEl) audioEl.muted = inboundRinging;
  if (!call || call.answeredAt !== null || call.state === "talking") {
    soundSystem.stop();
    return;
  }
  if (call.direction === "outbound") {
    // Yeastar liefert ausgehend (v. a. ins PSTN) den Ringback häufig als In-Band-
    // Early-Media auf session.remoteStream — schon VOR der Annahme. setStream()
    // hängt ihn dann ans <audio> und spielt ihn ab. Liegt solches Live-Remote-Audio
    // an, KEINEN synthetischen 425-Hz-Ton dazumischen (sonst zwei leicht verstimmte
    // Quellen → Schwebung); das PBX-Ringback durchlassen. Sonst synthetisch klingeln.
    const session = activeCallId && phone ? phone.getSession(activeCallId) : null;
    if (hasLiveRemoteAudio(session)) soundSystem.stop();
    else soundSystem.startRingback();
  } else {
    // Eingehend liegt vor der Annahme kein Remote-Audio an → immer synthetisch.
    soundSystem.startRingtone();
  }
}

function refresh(): void {
  const session = activeCallId && phone ? phone.getSession(activeCallId) : null;
  const call = toCall(session);
  if (call) {
    const eintrag = historieFuer(call.id);
    if (eintrag && eintrag.ende === null) eintrag.zustand = call.state;
  }
  emit({ activeCall: call });
  syncRingTones();
}

/**
 * Report a finished call to the backend ONCE, with final status + durations, so
 * a CallLog row is created from the SDK (parallel to the CDR webhook). Best-effort:
 * the webhook stays as the fallback, so a failed report never disrupts the call.
 */
function reportCallEnded(callId: string): void {
  const meta = callMeta.get(callId);
  callMeta.delete(callId);
  if (!meta) return;
  const end = Date.now();
  const callDurationSec = Math.max(0, Math.round((end - meta.startedAt) / 1000));
  const talkDurationSec = meta.answeredAt
    ? Math.max(0, Math.round((end - meta.answeredAt) / 1000))
    : 0;
  const ergebnis = telephonyService
    .ingestCall({
      callId,
      direction: meta.direction,
      fromNumber: meta.direction === "inbound" ? meta.number ?? undefined : undefined,
      toNumber: meta.direction === "outbound" ? meta.number ?? undefined : undefined,
      status: meta.answeredAt ? "ANSWERED" : "NO ANSWER",
      startedAt: new Date(meta.startedAt).toISOString(),
      callDurationSec,
      talkDurationSec,
    })
    .then((r) => (typeof r?.callLogId === "number" ? r.callLogId : null))
    .catch(() => {
      /* best-effort; der CDR-Webhook ist der Fallback */
      return null;
    });
  callLogIds.set(callId, ergebnis);
  while (callLogIds.size > CALL_LOG_IDS_MAX) {
    const aeltester = callLogIds.keys().next().value;
    if (aeltester === undefined) break;
    callLogIds.delete(aeltester);
  }
  endedListeners.forEach((l) => {
    try {
      l(callId);
    } catch {
      /* ein fehlerhafter Zuhörer darf das Auflegen nicht stören */
    }
  });
}

// Phone-Events liefern je nach SDK-Version `callId` ODER `{ callId, session }`.
function extractCallId(arg: unknown): string | null {
  if (!arg) return null;
  if (typeof arg === "string") return arg;
  const o = arg as { callId?: string; session?: { status?: { callId?: string } } };
  return o.callId ?? o.session?.status?.callId ?? null;
}
function extractSession(arg: unknown, callId: string | null): Session | null {
  const o = arg as { session?: Session };
  if (o && typeof o === "object" && o.session) return o.session;
  return callId && phone ? phone.getSession(callId) : null;
}

/** Per-Call-Tracking (Listener-Sperre + Fallback-Timer) am Call-Ende abräumen. */
function cleanupCall(callId: string): void {
  attachedCallIds.delete(callId);
  const t = fallbackTimers.get(callId);
  if (t !== undefined) {
    window.clearTimeout(t);
    fallbackTimers.delete(callId);
  }
  const h = hangupTimers.get(callId);
  if (h !== undefined) {
    window.clearTimeout(h);
    hangupTimers.delete(callId);
  }
  const k = keinTonTimers.get(callId);
  if (k !== undefined) {
    window.clearTimeout(k);
    keinTonTimers.delete(callId);
  }
}

/**
 * Einheitlicher Session-Ende-Pfad (P5): Tracking abräumen, CallLog melden und —
 * falls es noch der aktive Call ist — die aktive Session wechseln/leeren. Wird
 * von ALLEN End-Events (ended/failed/bye/terminated) UND vom Auflege-Fallback
 * aufgerufen; mehrfacher Aufruf ist unkritisch (reportCallEnded ist über callMeta
 * idempotent, cleanupCall ist idempotent).
 */
function endCall(callId: string): void {
  const eintrag = historieFuer(callId);
  if (eintrag && eintrag.ende === null) {
    eintrag.ende = new Date().toISOString();
    eintrag.zustand = "beendet";
  }
  cleanupCall(callId);
  reportCallEnded(callId);
  if (activeCallId === callId) clearActive();
  rueckfrageNachEnde(callId);
}

/**
 * Ein Gespräch der Rückfrage ist zu Ende:
 *  - nach „Verbinden": Übergabe gelungen, Rückfrage vorbei;
 *  - Kollege legt auf / nimmt nicht an: zurück zum Anrufer (Halten lösen);
 *  - Anrufer legt auf: das Gespräch mit dem Kollegen läuft normal weiter.
 */
function rueckfrageNachEnde(callId: string): void {
  const r = state.rueckfrage;
  if (!r || (callId !== r.anruferCallId && callId !== r.rueckfrageCallId)) return;
  if (r.verbindetSeit != null) {
    uebergebeneIds.add(r.anruferCallId);
    console.info("[telephony] Rückfrage: verbunden", { anrufer: r.anruferCallId, ziel: r.ziel });
    emit({ rueckfrage: null });
    return;
  }
  if (callId === r.rueckfrageCallId && phone?.getSession(r.anruferCallId)) {
    console.info("[telephony] Rückfrage beendet — zurück zum Anrufer", { anrufer: r.anruferCallId });
    zurueckZumAnrufer(r.anruferCallId);
  }
  emit({ rueckfrage: null });
}

function zurueckZumAnrufer(anruferCallId: string): void {
  if (!phone) return;
  if (activeCallId !== anruferCallId && phone.getSession(anruferCallId)) attachSession(anruferCallId);
  try {
    phone.unhold(anruferCallId);
  } catch {
    /* Halten bleibt — der Knopf „Fortsetzen" im Fenster löst es */
  }
  refresh();
}

/**
 * Auflege-Fallback bewaffnen (P5): Wenn nach hangup()/reject() binnen
 * HANGUP_FALLBACK_MS KEIN End-Event vom SDK kommt, den Call hart abräumen, damit
 * das Popup garantiert verschwindet. Pro callId nur ein Timer.
 */
function scheduleHangupFallback(callId: string): void {
  if (hangupTimers.has(callId)) return;
  const t = window.setTimeout(() => {
    hangupTimers.delete(callId);
    // Schon sauber beendet (Event kam doch) → nichts zu tun.
    if (activeCallId !== callId && !callMeta.has(callId)) return;
    endCall(callId);
  }, HANGUP_FALLBACK_MS);
  hangupTimers.set(callId, t);
}

function attachSession(arg: unknown): void {
  if (!phone) return;
  const callId = extractCallId(arg);
  if (!callId) return;
  const session = extractSession(arg, callId);
  if (!session) return;
  activeCallId = callId;
  const r = state.rueckfrage;
  if (r && !r.rueckfrageCallId && callId !== r.anruferCallId) {
    rueckfrageIds.add(callId);
    emit({ rueckfrage: { ...r, rueckfrageCallId: callId } });
  }
  const audio = ensureAudio();
  // Robuste MediaStream-Auflösung: manche SDK-Events liefern KEINEN MediaStream, sondern
  // z. B. ein RTCTrackEvent/Wrapper — so feuert `updateRemoteStream` aus dem `set remoteStream`-
  // Setter beim ontrack. `audio.srcObject = <kein MediaStream>` wirft dann einen TypeError, der
  // track-tragende Stream wird nie angehängt → man hört nichts (Live-Bug: audioTracks:0 + Crash).
  // Daher erst den Kandidaten selbst prüfen, dann gängige Felder (.stream/.streams[0]), zuletzt
  // session.remoteStream (nach ontrack mit dem Audio-Track befüllt) als verlässlichen Fallback.
  const resolveStream = (s: unknown): MediaStream | null => {
    if (s instanceof MediaStream) return s;
    const field = (s as { stream?: unknown } | null | undefined)?.stream;
    if (field instanceof MediaStream) return field;
    const first = (s as { streams?: unknown[] } | null | undefined)?.streams?.[0];
    if (first instanceof MediaStream) return first;
    return session.remoteStream instanceof MediaStream ? session.remoteStream : null;
  };
  const setStream = (s: unknown, reason: string) => {
    const stream = resolveStream(s);
    if (!stream) return;
    if (audio.srcObject !== stream) audio.srcObject = stream;
    const eintrag = historieFuer(callId);
    if (eintrag) {
      const { gesamt, live } = zaehleTracks(stream);
      eintrag.tracksMax = Math.max(eintrag.tracksMax, gesamt);
      eintrag.tracksLiveMax = Math.max(eintrag.tracksLiveMax, live);
    }
    console.debug(`[telephony] remoteStream gesetzt (${reason})`, {
      callId,
      audioTracks: stream.getAudioTracks?.().length ?? 0,
      paused: audio.paused,
      readyState: audio.readyState,
    });
    // Nach JEDEM Setzen explizit abspielen — sonst blockt die Autoplay-Policy die
    // asynchron (per Event) gesetzte Wiedergabe und der Gegenüber ist nicht hörbar.
    playAudio(reason);
    // Gate für ausgehendes Ringback neu bewerten: kommt jetzt (per Event) PBX-Early-
    // Media-Audio an, muss syncRingTones() den synthetischen Ton abschalten.
    refresh();
  };

  // Idempotent: dieselbe callId erreicht attachSession mehrfach (incoming/
  // newRTCSession/startSession feuern für EINEN Call; clearActive re-attacht die
  // verbleibende Session). Sind die Listener für diese callId bereits gebunden,
  // nur Audio-/State-Bezug auffrischen — KEINE Listener/Timer erneut anlegen.
  if (attachedCallIds.has(callId)) {
    setStream(session.remoteStream, "reattach");
    refresh();
    return;
  }
  attachedCallIds.add(callId);

  const tracked = toCall(session);
  if (tracked && !callMeta.has(callId)) {
    callMeta.set(callId, {
      startedAt: Date.now(),
      answeredAt: null,
      direction: tracked.direction,
      number: tracked.number,
    });
  }
  if (tracked) merkeAnruf(callId, tracked.direction, tracked.number, tracked.state);
  setStream(session.remoteStream, "initial");
  // `streamAdded` ist die zuverlässig dokumentierte Audio-Quelle; updateRemoteStream
  // + peerconnection als Fallback (je nach SDK-Version feuert nicht jedes Event).
  session.on("streamAdded", (payload: unknown) => setStream(payload, "streamAdded"));
  session.on("updateRemoteStream", (s: unknown) => setStream(s, "updateRemoteStream"));
  session.on("peerconnection", () => setStream(session.remoteStream, "peerconnection"));
  // Fallback: Falls KEIN Stream-Event feuert (SDK-Versionsunterschiede), den
  // remoteStream der Session nach kurzer Verzögerung nachziehen + erneut abspielen.
  const fallbackTimer = window.setTimeout(() => {
    fallbackTimers.delete(callId);
    if (activeCallId !== callId) return;
    if (audio.srcObject !== session.remoteStream) setStream(session.remoteStream, "fallback-timeout");
    else playAudio("fallback-replay");
  }, 1200);
  fallbackTimers.set(callId, fallbackTimer);
  const markAnswered = () => {
    const m = callMeta.get(callId);
    if (m && m.answeredAt === null) {
      m.answeredAt = Date.now();
      const eintrag = historieFuer(callId);
      if (eintrag && eintrag.angenommen === null) eintrag.angenommen = new Date(m.answeredAt).toISOString();
      planeKeinTonPruefung(callId, m.answeredAt);
    }
    refresh();
  };
  session.on("statusChange", () => refresh());
  session.on("accepted", markAnswered);
  session.on("confirmed", markAnswered);
  // ALLE bekannten End-Events auf den einen Ende-Pfad mappen (P5). Je nach
  // SDK-/SIP-Version feuert nicht jedes davon; doppeltes Feuern ist unkritisch
  // (endCall ist idempotent). `on` ist tolerant gegen unbekannte Event-Namen,
  // zur Sicherheit dennoch gekapselt.
  // `clientError` beendet die Session im SDK ebenso (T-233) — bisher räumte
  // dann erst der Fallback ab.
  for (const ev of ["ended", "failed", "clientError", "bye", "terminated"]) {
    try {
      // `bye`/`terminated` stehen NICHT in `SessionEventType` des SDK — genau
      // deshalb steht der Aufruf im try/catch. Der Cast macht sichtbar, dass das
      // Absicht ist und kein vergessener Tippfehler.
      session.on(ev as Parameters<typeof session.on>[0], (payload?: unknown) => {
        const grund = endeGrundAus(ev, payload);
        const eintrag = historieFuer(callId);
        if (eintrag && eintrag.endeGrund === null && grund) {
          eintrag.endeGrund = grund;
          console.info("[telephony] Anruf beendet", { callId, grund });
        }
        endCall(callId);
      });
    } catch {
      /* SDK kennt dieses Event evtl. nicht — ignorieren */
    }
  }
  refresh();
}

function clearActive(): void {
  // Prefer any other still-open session, else clear.
  const remaining = phone?.getSessions().find((s) => s.status.callId !== activeCallId);
  activeCallId = remaining?.status.callId ?? null;
  if (activeCallId) attachSession(activeCallId);
  else {
    if (audioEl) {
      audioEl.srcObject = null;
      audioEl.muted = false; // Inbound-Klingel-Stumm nicht über den Anruf hinaus halten
    }
    soundSystem.stop();
    emit({ activeCall: null });
  }
}

export const softphone = {
  getState(): SoftphoneState {
    return state;
  },
  subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },

  /** Idempotent: fetch credentials, load + init the SDK, register over WSS. */
  async connect(): Promise<void> {
    if (connecting || phone) return;
    connecting = true;
    cancelConnect = false;
    emit({ status: "connecting", error: null });
    try {
      const mod = await import("ys-webrtc-sdk-core");
      if (cancelConnect) return;
      const creds = await telephonyService.sdkCredentials();
      if (cancelConnect) return;
      const { phone: ph, destroy } = await mod.init({
        username: creds.username,
        secret: creds.secret,
        pbxURL: creds.pbxURL,
        deviceIds: mikrofonId ? { microphoneId: mikrofonId } : undefined,
      });
      // Wurde währenddessen disconnect() aufgerufen, die SDK-Instanz sofort wieder
      // abräumen — sonst bliebe ein registriertes Softphone ohne teardown übrig.
      if (cancelConnect) {
        try {
          destroy();
        } catch {
          /* ignore */
        }
        return;
      }
      phone = ph;
      teardown = destroy;

      ph.on("registered", () => emit({ status: "registered" }));
      // Transport-/Registrierungs-Tod (WSS-Abbruch, WLAN-Wechsel, Standby, Netz-Blip):
      // Das SDK feuert hier KEIN Session-End-Event, und der Auflege-Fallback greift nur
      // nach explizitem hangup()/reject(). Ohne aktives Abräumen bliebe ein laufender
      // Call mit totem Transport hängen (Popup offen, Buttons funktionslos, Ruftöne an).
      // Darum den aktiven Call hart beenden (endCall → cleanupCall + CallLog + clearActive,
      // letzteres stoppt soundSystem + leert das Audio), bevor der Fehlerstatus gesetzt wird.
      const dropActiveOnTransportDeath = () => {
        if (activeCallId) endCall(activeCallId);
        else {
          soundSystem.stop();
          if (audioEl) {
            audioEl.srcObject = null;
            audioEl.muted = false;
          }
        }
      };
      ph.on("registrationFailed", () => {
        dropActiveOnTransportDeath();
        emit({ status: "error", error: "Registrierung fehlgeschlagen" });
      });
      ph.on("disconnected", () => {
        dropActiveOnTransportDeath();
        emit({ status: "error", error: "Verbindung getrennt" });
      });
      ph.on("incoming", (arg: unknown) => attachSession(arg));
      ph.on("newRTCSession", (arg: unknown) => attachSession(arg));
      ph.on("startSession", (arg: unknown) => attachSession(arg));
      ph.on("deleteSession", (arg: unknown) => {
        const id = extractCallId(arg);
        if (id) endCall(id);
        else if (activeCallId) clearActive();
      });

      ph.start();
      if (ph.isRegistered) emit({ status: "registered" });
    } catch (e) {
      if (!cancelConnect) emit({ status: "error", error: (e as Error).message });
    } finally {
      connecting = false;
    }
  },

  disconnect(): void {
    // Bricht eine evtl. noch laufende connect()-Sequenz ab (siehe cancelConnect).
    cancelConnect = true;
    try {
      teardown?.();
    } catch {
      /* ignore */
    }
    phone = null;
    teardown = null;
    activeCallId = null;
    // Ausstehende Fallback-Timer (Audio + Auflegen) + Listener-Sperre abräumen,
    // damit nach dem Teardown kein verwaister Timer mehr feuert und der nächste
    // connect() sauber startet.
    fallbackTimers.forEach((t) => window.clearTimeout(t));
    fallbackTimers.clear();
    hangupTimers.forEach((t) => window.clearTimeout(t));
    hangupTimers.clear();
    keinTonTimers.forEach((t) => window.clearTimeout(t));
    keinTonTimers.clear();
    attachedCallIds.clear();
    soundSystem.stop();
    if (audioEl) {
      audioEl.srcObject = null;
      audioEl.muted = false;
    }
    state = initialState;
    listeners.forEach((l) => l());
  },

  // ── call control (Phase 3 + 4) ──
  async dial(number: string): Promise<void> {
    if (!phone) return;
    // Autoplay-Gate im Gesten-Fenster überbrücken: das Audio-Element JETZT (noch
    // während der User-Geste) „entsperren". Der Remote-Stream kommt erst per Event;
    // der spätere play() im Event greift dann ohne Autoplay-Block.
    ensureAudio();
    playAudio("dial-gesture");
    // WebAudio-Context SYNCHRON in der Geste anlegen/resumen, damit das ausgehende
    // Ringback (startRingback() erst im späteren SDK-Event) auf Safari/iOS hörbar ist.
    soundSystem.prime();
    await phone.call(number);
  },
  /**
   * „Annehmen" — mit benanntem Ergebnis statt stillem No-Op (T-233, siehe
   * `softphoneAnnehmen.ts`). Wirft nie; der Aufrufer zeigt `ok: false` an.
   */
  async answer(): Promise<AnnehmenErgebnis> {
    const callId = activeCallId;
    // Doppelklick: Der Knopf bleibt sichtbar, bis das SDK „accepted" meldet.
    // Ein zweites answer() auf denselben Anruf weist das SDK ab — das Gespräch
    // läuft trotzdem. Also gar nicht erst ans SDK geben (07.10.2026, ic@).
    if (callId && (annehmenLaeuft.has(callId) || callMeta.get(callId)?.answeredAt != null)) {
      console.info("[telephony] Annehmen ignoriert — läuft schon oder ist angenommen", { callId });
      return { ok: true };
    }
    const eintrag = callId ? historieFuer(callId) : undefined;
    if (eintrag) {
      eintrag.annehmenGeklickt = new Date().toISOString();
      eintrag.annehmenErgebnis = null;
    }
    console.info("[telephony] Annehmen geklickt", { callId, sdkStatus: state.status, hatTelefon: !!phone });
    if (phone && callId) {
      // Siehe dial(): play() im Gesten-Fenster, damit die Wiedergabe „freigeschaltet" ist.
      ensureAudio();
      playAudio("answer-gesture");
      // Context im Gesten-Fenster vorbereiten (s. dial()); konsistent mit dem Audio-Prime.
      soundSystem.prime();
    }
    if (callId) annehmenLaeuft.add(callId);
    let ergebnis: AnnehmenErgebnis;
    try {
      ergebnis = await fuehreAnnehmenAus({ phone, callId, sdkStatus: state.status });
    } finally {
      if (callId) annehmenLaeuft.delete(callId);
    }
    if (eintrag) eintrag.annehmenErgebnis = ergebnisText(ergebnis);
    if (!ergebnis.ok) {
      console.warn(`[telephony] Annehmen fehlgeschlagen: ${ergebnis.grund}`, { callId });
      return ergebnis;
    }
    console.info("[telephony] Annehmen an das Softphone übergeben", { callId });
    // Kommt danach keine Bestätigung (accepted/confirmed), steht der Anruf still
    // weiter auf „klingelt" — genau der Fall, den T-233 nicht belegen konnte.
    if (callId) {
      window.setTimeout(() => {
        const m = callMeta.get(callId);
        if (!m || m.answeredAt !== null) return;
        const e = historieFuer(callId);
        if (e && e.annehmenErgebnis === "angenommen") {
          e.annehmenErgebnis = e.ende
            ? "Anruf endete, bevor das Softphone die Annahme bestätigte"
            : `keine Bestätigung nach ${ANNAHME_BESTAETIGUNG_MS / 1000} s`;
        }
        console.warn("[telephony] Annehmen ohne Bestätigung", { callId, zustand: e?.zustand, ende: e?.ende });
      }, ANNAHME_BESTAETIGUNG_MS);
    }
    return ergebnis;
  },
  /** Liegt gerade ein Klick auf „Annehmen" beim SDK? (Tischtelefon-Entscheidung, `tischtelefon.ts`) */
  annehmenLaeuft(): boolean {
    return annehmenLaeuft.size > 0;
  },
  hangup(): void {
    if (!phone || !activeCallId) return;
    const id = activeCallId;
    try {
      phone.hangup(id);
    } catch {
      /* selbst wenn der SDK-Call wirft: der Fallback unten räumt sicher ab */
    }
    // Optimistisch: Falls KEIN ended/failed/bye/terminated-Event kommt, den Call
    // nach kurzer Frist hart abräumen — kein hängendes Popup (P5).
    scheduleHangupFallback(id);
  },
  reject(): void {
    if (!phone || !activeCallId) return;
    const id = activeCallId;
    try {
      phone.reject(id);
    } catch {
      /* s. hangup(): der Fallback sichert das Abräumen ab */
    }
    scheduleHangupFallback(id);
  },
  mute(): void {
    if (phone && activeCallId) phone.mute(activeCallId);
    refresh();
  },
  unmute(): void {
    if (phone && activeCallId) phone.unmute(activeCallId);
    refresh();
  },
  hold(): void {
    if (phone && activeCallId) phone.hold(activeCallId);
    refresh();
  },
  unhold(): void {
    if (phone && activeCallId) phone.unhold(activeCallId);
    refresh();
  },
  dtmf(digit: string): void {
    if (phone && activeCallId) phone.dtmf(activeCallId, digit);
  },
  blindTransfer(number: string): void {
    if (phone && activeCallId) phone.blindTransfer(activeCallId, number);
  },
  /** Rückfrage beginnen: Anrufer halten, `ziel` anrufen. false = ging nicht. */
  async rueckfrageStarten(ziel: string): Promise<boolean> {
    if (!phone || !activeCallId || state.rueckfrage) return false;
    const anrufer = activeCallId;
    const anruferNummer = state.activeCall?.number ?? null;
    ensureAudio();
    playAudio("rueckfrage-gesture");
    soundSystem.prime();
    try {
      phone.hold(anrufer);
    } catch {
      /* ohne Halten hört der Anrufer mit — trotzdem weiter */
    }
    emit({
      rueckfrage: { anruferCallId: anrufer, anruferNummer, ziel, rueckfrageCallId: null, verbindetSeit: null, fehler: null },
    });
    console.info("[telephony] Rückfrage gestartet", { anrufer, ziel });
    try {
      // Das SDK meldet manche Fehler als Ergebnis mit negativem Code statt als Abweisung.
      const ergebnis = (await phone.call(ziel, undefined, anrufer)) as { code?: number; message?: string } | undefined;
      if (typeof ergebnis?.code === "number" && ergebnis.code < 0) throw ergebnis;
      return true;
    } catch (err) {
      console.warn("[telephony] Rückfrage: Anruf beim Kollegen fehlgeschlagen", err);
      emit({ rueckfrage: null });
      zurueckZumAnrufer(anrufer);
      return false;
    }
  },
  /** Anrufer mit dem Kollegen verbinden (nach der Rückfrage). */
  rueckfrageVerbinden(): void {
    const r = state.rueckfrage;
    if (!phone || !r?.rueckfrageCallId || r.verbindetSeit != null) return;
    let ok = false;
    try {
      ok = phone.attendedTransfer(r.rueckfrageCallId, r.ziel);
    } catch (err) {
      console.warn("[telephony] Rückfrage: Verbinden fehlgeschlagen", err);
    }
    if (!ok) {
      emit({ rueckfrage: { ...r, fehler: "Verbinden ging nicht. Der Anrufer wartet weiter." } });
      return;
    }
    const seit = Date.now();
    emit({ rueckfrage: { ...r, verbindetSeit: seit, fehler: null } });
    // Bestätigt wird das Verbinden dadurch, dass die Anlage beide Gespräche
    // beendet. Bleibt das aus, nichts auflegen (sonst fliegt der Anrufer
    // raus) — nur sagen, dass es nicht geklappt hat.
    window.setTimeout(() => {
      const jetzt = state.rueckfrage;
      if (!jetzt || jetzt.verbindetSeit !== seit) return;
      console.warn("[telephony] Rückfrage: Anlage hat das Verbinden nicht bestätigt", { anrufer: r.anruferCallId });
      emit({ rueckfrage: { ...jetzt, verbindetSeit: null, fehler: "Die Anlage hat nicht verbunden. Der Anrufer wartet noch." } });
    }, VERBINDEN_FRIST_MS);
  },
  /** Rückfrage abbrechen: Gespräch mit dem Kollegen beenden, zurück zum Anrufer. */
  rueckfrageAbbrechen(): void {
    const r = state.rueckfrage;
    if (!r) return;
    emit({ rueckfrage: null });
    if (phone && r.rueckfrageCallId && phone.getSession(r.rueckfrageCallId)) {
      try {
        phone.hangup(r.rueckfrageCallId);
      } catch {
        /* Fallback räumt ab */
      }
      scheduleHangupFallback(r.rueckfrageCallId);
    }
    zurueckZumAnrufer(r.anruferCallId);
  },
  /** War `callId` ein Rückfragegespräch dieses Tabs? (keine Nach-Anruf-Karte) */
  warRueckfrage(callId: string): boolean {
    return rueckfrageIds.has(callId);
  },
  /** Wurde der Anrufer per Rückfrage übergeben? */
  wurdeUebergeben(callId: string): boolean {
    return uebergebeneIds.has(callId);
  },

  /**
   * Die `CallLog`-ID eines beendeten Anrufs, sobald der Ingest geantwortet hat.
   * `null` = für diese callId wurde (in diesem Tab) nichts gemeldet; das
   * Promise liefert `null`, wenn der Ingest scheiterte.
   */
  callLogIdFor(callId: string): Promise<number | null> | null {
    return callLogIds.get(callId) ?? null;
  },

  /** Benachrichtigt, sobald ein Gespräch endet (s. `endedListeners`). */
  onCallEnded(listener: (callId: string) => void): () => void {
    endedListeners.add(listener);
    return () => endedListeners.delete(listener);
  },

  /**
   * Telefonie-Diagnose für das Ticket (T-28): SDK-Status, die letzten Anrufe
   * dieses Tabs und der Zustand des Remote-Audios. Liefert Kopien; wirft nie.
   */
  diagnose(): SoftphoneDiagnose {
    let registriert: boolean | null = null;
    try {
      registriert = phone ? !!phone.isRegistered : null;
    } catch {
      /* ignore */
    }
    let audio: AudioZustand | null = null;
    try {
      audio = audioZustand();
    } catch {
      /* ignore */
    }
    return {
      status: state.status,
      fehler: state.error,
      registriert,
      aktiverAnruf: activeCallId,
      anrufe: anrufHistorie.map((e) => ({ ...e, audioNachAnnahme: e.audioNachAnnahme ? { ...e.audioNachAnnahme } : null })),
      audio,
    };
  },

  /** Zustand des Remote-Audios jetzt (für den „Kein Ton?"-Hinweis im Anruf-Fenster). */
  audioZustand(): AudioZustand | null {
    try {
      return audioZustand();
    } catch {
      return null;
    }
  },

  /**
   * „Ton erneut starten" aus dem Anruf-Fenster: den Remote-Stream der aktiven
   * Session (wieder) anhängen, Stummschaltung des Elements lösen und im
   * Gesten-Fenster des Klicks play() versuchen.
   */
  tonNeuStarten(): void {
    const audio = ensureAudio();
    const session = activeCallId && phone ? phone.getSession(activeCallId) : null;
    if (session?.remoteStream instanceof MediaStream && audio.srcObject !== session.remoteStream) {
      audio.srcObject = session.remoteStream;
    }
    audio.muted = false;
    console.info("[telephony] Ton erneut starten (Nutzer)", { callId: activeCallId, ...audioZustand() });
    playAudio("nutzer-neustart");
  },

  /**
   * Mikrofon fürs Gespräch ("" = Standard). Gilt für die nächsten Anrufe und
   * wird im laufenden Gespräch sofort getauscht.
   */
  async setzeMikrofon(id: string): Promise<void> {
    if (id === mikrofonId) return;
    mikrofonId = id;
    if (phone) phone.deviceIds = { ...phone.deviceIds, microphoneId: id || undefined };
    try {
      await tauscheMikrofonImGespraech();
    } catch (e) {
      console.warn("[telephony] Mikrofon-Wechsel im Gespräch fehlgeschlagen", { id, fehler: String(e) });
    }
  },

  /** Lautsprecher fürs Gespräch ("" = Standard), sofort auch im laufenden Gespräch. */
  setzeLautsprecher(id: string): void {
    lautsprecherId = id;
    if (audioEl) wendeLautsprecherAn(audioEl);
  },

  /** Phase 5: the active call's media streams (local mic + remote audio). */
  getActiveStreams(): { local: MediaStream | null; remote: MediaStream | null } {
    const s = activeCallId && phone ? phone.getSession(activeCallId) : null;
    return { local: s?.localStream ?? null, remote: s?.remoteStream ?? null };
  },
};

/** React binding for the softphone singleton. */
export function useSoftphone(): SoftphoneState {
  return useSyncExternalStore(softphone.subscribe, softphone.getState, softphone.getState);
}
